/**
 * Hariom ERP - Login Page
 * Full-screen branded login with company logo and credentials
 */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Lock, Mail, Loader2 } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import type { User } from '../../types'
import toast from 'react-hot-toast'

/**
 * ===== DEMO USER ACCOUNTS =====
 *  1) Founder            → role "admin"     → full access to every page.
 *  2) Shoaib             → role "marketing" → ONLY the Demand Forecast page.
 *  3) Ved Prakash        → role "marketing" → ONLY the Demand Forecast page.
 *  4) Ashish             → role "purchase"  → ONLY the Purchase page.
 *  5) Amris              → role "purchase"  → ONLY the Purchase page.
 *
 * Page-level access is enforced in App.tsx (routing) and the Sidebar.
 * NOTE: passwords are kept here only for the pre-launch demo. Before hosting,
 * move authentication to the backend (/auth/login) and remove these.
 */
interface DemoAccount { email: string; password: string; user: User }

const DEMO_USERS: DemoAccount[] = [
  {
    email: 'rahul@jp.com',
    password: 'Rahul@123',
    user: { id: 'u-rahul', name: 'Rahul', email: 'rahul@jp.com',
            role: 'admin', department: 'Management', createdAt: new Date().toISOString() },
  },
  {
    email: 'shoaib@hariomepoxyshield.com',
    password: 'Shoaib@123',
    user: { id: 'u-shoaib', name: 'Shoaib', email: 'shoaib@hariomepoxyshield.com',
            role: 'marketing', department: 'Marketing', createdAt: new Date().toISOString() },
  },
  {
    email: 'ved@hariomepoxyshield.com',
    password: 'Ved@123',
    user: { id: 'u-ved', name: 'Ved Prakash', email: 'ved@hariomepoxyshield.com',
            role: 'marketing', department: 'Marketing', createdAt: new Date().toISOString() },
  },
  {
    email: 'ashish@jp.com',
    password: 'Ashish@123',
    user: { id: 'u-ashish', name: 'Ashish', email: 'ashish@jp.com',
            role: 'purchase', department: 'Purchase', createdAt: new Date().toISOString() },
  },
  {
    email: 'amris@jp.com',
    password: 'Amris@123',
    user: { id: 'u-amris', name: 'Amris', email: 'amris@jp.com',
            role: 'purchase', department: 'Purchase', createdAt: new Date().toISOString() },
  },
  {
    // Bhavesh → Sales Order AND Diamond Purchase.
    email: 'bhavesh@jp.com',
    password: 'Bhavesh@123',
    user: { id: 'u-bhavesh', name: 'Bhavesh', email: 'bhavesh@jp.com',
            role: 'sales', department: 'Sales', createdAt: new Date().toISOString() },
  },
  {
    // Vinayak → Sales Order only.
    email: 'vinayak@jp.com',
    password: 'Vinayak@123',
    user: { id: 'u-vinayak', name: 'Vinayak', email: 'vinayak@jp.com',
            role: 'salesorder', department: 'Sales', createdAt: new Date().toISOString() },
  },
  {
    // Sunil → Sales Order only.
    email : 'sunil@jp.com',
    password: 'Sunil@123',
    user: { id: 'u-sunil', name: 'Sunil', email: 'sunil@jp.com',
            role: 'salesorder', department: 'Sales', createdAt: new Date().toISOString() },
  },
  {
    // Accountant 1 → Debtor Report only (founder's accountant).
    email: 'account1@jp.com',
    password: 'Account@123',
    user: { id: 'u-account1', name: 'Accountant 1', email: 'account1@jp.com',
            role: 'accountant', department: 'Accounts', createdAt: new Date().toISOString() },
  },
  {
    // Accountant 2 → Debtor Report only (founder's accountant).
    email: 'account2@jp.com',
    password: 'Account@123',
    user: { id: 'u-account2', name: 'Accountant 2', email: 'account2@jp.com',
            role: 'accountant', department: 'Accounts', createdAt: new Date().toISOString() },
  }
]

// Where each role lands after login (their first allowed page, if restricted).
const ROLE_HOME: Record<string, string> = {
  marketing: '/forecast', purchase: '/purchase', sales: '/sales-order', salesorder: '/sales-order',
  accountant: '/debtor',
}

const Login: React.FC = () => {
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd]   = useState(false)
  const [loading, setLoading]   = useState(false)
  const { setUser } = useAuthStore()
  const navigate = useNavigate()

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    // Simulate API — replace with: apiClient.post('/auth/login', {email, password})
    await new Promise(r => setTimeout(r, 800))

    // Match against the demo accounts (email case-insensitive, exact password).
    const account = DEMO_USERS.find(
      u => u.email.toLowerCase() === email.trim().toLowerCase() && u.password === password
    )

    if (account) {
      setUser(account.user, `mock-jwt-${account.user.role}`)
      toast.success(`Welcome, ${account.user.name}!`)
      // Restricted roles land straight on their department page; others on Dashboard.
      navigate(ROLE_HOME[account.user.role] || '/dashboard')
    } else {
      toast.error('Invalid email or password')
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen flex">
      {/* LEFT: Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 gradient-brand flex-col items-center justify-center p-12 text-white">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-black text-4xl">H</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight">HARIOM</h1>
          <p className="text-red-200 font-semibold tracking-widest text-sm mt-1">EPOXYSHIELD ERP</p>
        </div>
        <div className="text-center max-w-md">
          <p className="text-white/90 text-lg font-medium mb-2">Enterprise Resource Planning</p>
          <p className="text-white/60 text-sm leading-relaxed">
            Built exclusively for Hariom Coatings Pvt Ltd — managing production, demand forecasting,
            inventory and quality of India's leading epoxy coated steel manufacturer.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-3 gap-6 text-center">
          {[['Since','1995'],['Capacity','1L TPY'],['Countries','12+']].map(([l,v])=>(
            <div key={l}><p className="text-2xl font-black">{v}</p><p className="text-red-200 text-xs mt-0.5">{l}</p></div>
          ))}
        </div>
      </div>

      {/* RIGHT: Login Form */}
      <div className="flex-1 flex items-center justify-center bg-steel-50 p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 gradient-brand rounded-2xl flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-black text-3xl">H</span>
            </div>
            <h2 className="text-xl font-black text-steel-900">HARIOM EPOXYSHIELD ERP</h2>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-steel-100">
            <h2 className="text-2xl font-bold text-steel-900 mb-1">Sign in</h2>
            <p className="text-steel-500 text-sm mb-6">Enter your credentials to access the ERP</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-steel-700 block mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-400"/>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                    className="form-input pl-9" placeholder="you@hariomepoxyshield.com" required/>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-steel-700 block mb-1.5">Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-400"/>
                  <input type={showPwd?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)}
                    className="form-input pl-9 pr-10" placeholder="••••••••" required/>
                  <button type="button" onClick={()=>setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-400 hover:text-steel-600">
                    {showPwd?<EyeOff size={16}/>:<Eye size={16}/>}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 text-base mt-2">
                {loading?<><Loader2 size={18} className="animate-spin"/>Signing in...</>:'Sign In to ERP'}
              </button>
            </form>
            {/* Secure sign-in. User accounts are managed in Settings → Users.
                (Demo credential hints removed for security.) */}
            <div className="mt-4 p-3 bg-steel-50 rounded-lg border border-steel-100">
              <p className="text-xs text-steel-500">
                Use your company email and password to sign in. Contact your
                administrator if you need access.
              </p>
            </div>
          </div>
          <p className="text-center text-xs text-steel-400 mt-6">© 2026 Hariom Coatings Private Limited · ERP v1.0.0</p>
          <p className="text-center text-xs text-steel-400 mt-1">Designed &amp; Developed by <span className="font-semibold text-steel-500">Mohammed Nazim</span></p>
        </div>
      </div>
    </div>
  )
}

export default Login
